import React, { useState, useRef, useEffect } from "react";
import { MapPinIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { GOOGLE_MAPS_LOADER_OPTIONS } from "../utils/googleMaps";

export default function LocationPickerModal({ onShare, onClose, isSending }) {
  const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 };
  // currentCenterRef tracks the map center without triggering re-renders.
  // Only `initialCenter` state is passed to <GoogleMap center=...> so setting it
  // doesn't loop back through onCenterChanged.
  const currentCenterRef = useRef(DEFAULT_CENTER);
  const [initialCenter, setInitialCenter] = useState(DEFAULT_CENTER);
  const [label, setLabel] = useState("");
  const [geoError, setGeoError] = useState(null);
  const [relocating, setRelocating] = useState(false);
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const fetchGPS = (onSuccess) => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation is not supported by your browser.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        onSuccess(c);
      },
      (err) => {
        if (err.code === 1) {
          setGeoError("Location access denied. Enable it in your browser or iOS Settings, then try again.");
        } else {
          setGeoError("Could not get your location. Drag the map to position the pin manually.");
        }
      },
      { enableHighAccuracy: false, timeout: 20000, maximumAge: 60000 }
    );
  };

  useEffect(() => {
    fetchGPS((c) => {
      currentCenterRef.current = c;
      if (mapRef.current) {
        mapRef.current.panTo(c);
      } else {
        setInitialCenter(c);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRelocate = () => {
    setRelocating(true);
    fetchGPS((c) => {
      currentCenterRef.current = c;
      if (mapRef.current) mapRef.current.panTo(c);
      setRelocating(false);
    });
  };

  const handleCenterChanged = () => {
    if (mapRef.current) {
      const c = mapRef.current.getCenter();
      // Update ref only — no setState, so no re-render and no feedback loop.
      currentCenterRef.current = { lat: c.lat(), lng: c.lng() };
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">Share Your Spot</h3>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Map */}
        <div className="relative w-full h-56">
          {isLoaded ? (
            <>
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={initialCenter}
                zoom={15}
                onLoad={(m) => { mapRef.current = m; }}
                onCenterChanged={handleCenterChanged}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                  zoomControl: true,
                  gestureHandling: 'greedy',
                }}
              />
              {/* Fixed center pin */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="flex flex-col items-center" style={{ marginTop: '-24px' }}>
                  <svg width="28" height="36" viewBox="0 0 28 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 0C6.268 0 0 6.268 0 14c0 9.625 12.542 21.188 13.083 21.708a1.312 1.312 0 001.834 0C15.458 35.188 28 23.625 28 14 28 6.268 21.732 0 14 0z" fill="#EF4444"/>
                    <circle cx="14" cy="14" r="5" fill="white"/>
                  </svg>
                  <div className="w-1 h-2 bg-red-500 rounded-b" />
                </div>
              </div>
              {/* Hint */}
              <div className="absolute bottom-2 right-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-md px-2 py-1 text-[10px] text-gray-500 dark:text-gray-400 pointer-events-none">
                Drag map to position pin
              </div>
              {/* Relocate button */}
              <button
                onClick={handleRelocate}
                disabled={relocating}
                title="Go to my location"
                className="absolute bottom-2 left-2 w-[34px] h-[34px] rounded-full border-none bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm flex items-center justify-center cursor-pointer text-gray-700 dark:text-gray-200 shadow disabled:cursor-default disabled:opacity-70 hover:enabled:bg-white dark:hover:enabled:bg-gray-800 transition-colors"
              >
                {relocating ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-200 dark:border-gray-700 border-t-primary" />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 8a4 4 0 100 8 4 4 0 000-8zm8.94 3A8.994 8.994 0 0013 3.06V1h-2v2.06A8.994 8.994 0 003.06 11H1v2h2.06A8.994 8.994 0 0011 20.94V23h2v-2.06A8.994 8.994 0 0020.94 13H23v-2h-2.06zM12 19a7 7 0 110-14 7 7 0 010 14z"/>
                  </svg>
                )}
              </button>
            </>
          ) : (
            <div className="w-full h-full bg-gray-200 dark:bg-gray-700 animate-pulse flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          )}
        </div>

        <div className="px-4 py-3 flex flex-col gap-3">
          {geoError && (
            <p className="text-xs text-amber-600 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
              {geoError}
            </p>
          )}

          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value.slice(0, 60))}
            placeholder="Add a note – e.g. by the big oak tree"
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
          />

          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={isSending}
              className="flex-1 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onShare(currentCenterRef.current.lat, currentCenterRef.current.lng, label.trim() || null)}
              disabled={isSending || !isLoaded}
              className="flex-1 px-4 py-2 text-sm bg-primary text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-1"
            >
              {isSending ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <>
                  <MapPinIcon className="w-4 h-4" />
                  Share Location
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
